import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { GraduationCap, Send, Coins } from 'lucide-react';
import { toast } from 'sonner';
import { api, setStoredUser } from '@/utils/api';

const QUICK_REPLY_TEMPLATES = [
  'Hi, I can teach this subject.',
  'Sharing my available timings: 5 PM to 8 PM on weekdays.',
  'Please share your preferred start date and demo time.'
];

const formatMessageDateTime = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleString('en-IN', {
    timeZone: 'Asia/Kolkata',
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
};

export default function MessagesPage({ user }) {
  const location = useLocation();
  const directRecipientId = location.state?.recipientId || null;
  const directRecipientName = location.state?.recipientName || 'Tutor';
  const [currentCoins, setCurrentCoins] = useState(user?.coins || 0);
  const [conversations, setConversations] = useState([]);
  const [selectedUser, setSelectedUser] = useState(directRecipientId);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadConversations();
    loadCurrentCoins();
  }, []);

  useEffect(() => {
    // If navigated with a recipient, select that conversation
    if (directRecipientId) {
      setSelectedUser(directRecipientId);
      loadThread(directRecipientId);
    }
  }, [directRecipientId]);

  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [selectedConversation]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      loadConversations();
      if (selectedUser) {
        loadThread(selectedUser);
      }
    }, 30000);
    return () => clearInterval(intervalId);
  }, [selectedUser]);

  const loadConversations = async () => {
    try {
      setLoading(true);
      const response = await api.get('/messages/conversations');
      setConversations(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to load conversations:', error);
      setLoading(false);
    }
  };

  const loadCurrentCoins = async () => {
    try {
      const meRes = await api.get('/me');
      const coins = meRes?.data?.coins ?? user?.coins ?? 0;
      setCurrentCoins(coins);
      if (user) {
        setStoredUser({ ...user, coins });
      }
    } catch (_error) {
      setCurrentCoins(user?.coins || 0);
    }
  };

  const loadThread = async (partnerId) => {
    try {
      const response = await api.get(`/messages/thread/${partnerId}`);
      setSelectedConversation(response.data);
      await loadConversations();
    } catch (error) {
      console.error('Failed to load thread:', error);
    }
  };

  const handleSelectConversation = async (partnerId) => {
    if (directRecipientId) return;
    setSelectedUser(partnerId);
    await loadThread(partnerId);
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedUser) return;
    
    try {
      await api.post('/messages', {
        recipient_id: selectedUser,
        message: newMessage
      });
      if (user) {
        try {
          const meRes = await api.get('/me');
          const coins = meRes.data?.coins ?? user.coins;
          setCurrentCoins(coins);
          setStoredUser({ ...user, coins });
        } catch (_err) {
          // Ignore coin refresh failure.
        }
      }
      setNewMessage('');
      toast.success('Message sent!');
      // Reload conversation and thread
      loadConversations();
      loadThread(selectedUser);
    } catch (error) {
      if (error.response?.status === 402) {
        toast.error(error.response?.data?.detail || 'Insufficient coins. Please purchase coins first.');
      } else {
        toast.error('Failed to send message');
      }
    }
  };

  const applyQuickReply = (template) => {
    setNewMessage((prev) => {
      const current = String(prev || '').trim();
      return current ? `${current} ${template}` : template;
    });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="backdrop-blur-xl bg-white/70 border-b border-white/20 sticky top-0 z-50">
        <nav className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <GraduationCap className="w-8 h-8 text-indigo-600" />
            <span className="text-2xl font-outfit font-bold">Tricity Tutors</span>
          </Link>
          <div className="flex items-center gap-4">
            <Link to="/wallet">
              <Button data-testid="messages-wallet-btn" variant="outline" className="rounded-full flex items-center gap-2">
                <Coins className="w-4 h-4 text-yellow-600" />
                <span className="font-semibold">{currentCoins}</span>
              </Button>
            </Link>
            <Link to={user?.role === 'tutor' ? '/tutor/dashboard' : '/student/dashboard'}>
              <Button data-testid="messages-back-btn" variant="outline" className="rounded-full">
                Back to Dashboard
              </Button>
            </Link>
          </div>
        </nav>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-outfit font-bold mb-8">Messages</h1>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Conversations List */}
          {!directRecipientId && (
          <Card className="md:col-span-1">
            <CardHeader>
              <CardTitle>Conversations</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {loading ? (
                  <p className="text-center text-muted-foreground py-8 text-sm">Loading...</p>
                ) : conversations.length === 0 ? (
                  <p className="text-center text-muted-foreground py-8 text-sm">No messages yet</p>
                ) : (
                  conversations.map((convo) => (
                    <button
                      key={convo.partner_id}
                      data-testid={`conversation-${convo.partner_id}-btn`}
                      onClick={() => handleSelectConversation(convo.partner_id)}
                      className={`w-full p-3 rounded-lg text-left transition-colors ${
                        selectedUser === convo.partner_id
                          ? 'bg-primary text-primary-foreground'
                          : 'hover:bg-accent'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <Avatar>
                          <AvatarFallback>{convo.partner_name?.[0] || '?'}</AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className="font-semibold truncate">{convo.partner_name || 'Unknown'}</p>
                            {convo.unread_count > 0 && (
                              <span className="bg-red-500 text-white text-xs rounded-full px-2 py-1">
                                {convo.unread_count}
                              </span>
                            )}
                          </div>
                          <p className="text-sm truncate opacity-75">
                            {convo.last_message}
                          </p>
                          <p className="text-xs truncate opacity-65">
                            {formatMessageDateTime(convo.last_message_time)}
                          </p>
                        </div>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
          )}

          {/* Messages Display */}
          <Card className={directRecipientId ? 'md:col-span-3' : 'md:col-span-2'}>
            <CardHeader>
              <CardTitle>
                {selectedConversation?.partner
                  ? selectedConversation.partner.name
                  : (directRecipientId ? directRecipientName : 'Select a conversation')}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {selectedConversation ? (
                <div className="space-y-6">
                  {/* Messages */}
                  <div className="space-y-4 max-h-96 overflow-y-auto">
                    {selectedConversation.messages.map((msg) => (
                      <div
                        key={msg.id}
                        className={`flex ${msg.sender_id === user.id ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-xs p-3 rounded-lg ${
                            msg.sender_id === user.id
                              ? 'bg-primary text-primary-foreground'
                              : 'bg-accent'
                          }`}
                        >
                          <p className="text-sm">{msg.message}</p>
                          <p className="text-xs opacity-70 mt-1">
                            {formatMessageDateTime(msg.created_at)}
                          </p>
                        </div>
                      </div>
                    ))}
                    <div ref={messagesEndRef} />
                  </div>

                  {/* Send Message Form */}
                  {user?.role === 'tutor' && (
                    <div className="flex flex-wrap gap-2">
                      {QUICK_REPLY_TEMPLATES.map((template) => (
                        <Button
                          key={template}
                          type="button"
                          size="sm"
                          variant="outline"
                          className="rounded-full text-xs"
                          onClick={() => applyQuickReply(template)}
                        >
                          {template}
                        </Button>
                      ))}
                    </div>
                  )}
                  <form onSubmit={handleSendMessage} className="flex gap-2">
                    <Textarea
                      data-testid="message-input"
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                      placeholder="Type your message..."
                      rows={2}
                      className="flex-1"
                    />
                    <Button data-testid="send-message-btn" type="submit" className="rounded-full">
                      <Send className="w-4 h-4" />
                    </Button>
                  </form>
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-12">
                  Select a conversation to view messages
                </p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
